import { gql, type TypedDocumentNode } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { iconAngleLeft } from '@sorare/core/src/atoms/icons/system/iconAngleLeft.ts';
import { Centered } from '@sorare/core/src/atoms/layout/Centered';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { HeadlineS } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { CardSetChip } from '@sorare/core/src/components/CardSetChip';
import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useCloseDialog } from '@sorare/core/src/hooks/navigation/useCloseDialog';
import { useDialogParam } from '@sorare/core/src/hooks/navigation/useDialogParam';
import {
  CARD_ASPECT_RATIO,
  CARD_BORDER_RADIUS_2024,
} from '@sorare/core/src/lib/cards';

import { CardPageLayout } from '#src/components/CardPageLayout/index.tsx';

import { FindItInCardPacks } from './FindItInCardPacks/index.tsx';
import type {
  EmptyCollectionSlotContentQuery,
  EmptyCollectionSlotContentQueryVariables,
} from './__generated__/index.graphql.ts';
import questionMarkUrl from './assets/questionMark.svg';

const EmptyCardPlaceholder = styled(Vertical)`
  width: clamp(190px, 50%, 340px);
  border: 1px dashed var(--c-nd-150);
  background: url(${questionMarkUrl}) center no-repeat;
  aspect-ratio: ${CARD_ASPECT_RATIO};
  border-radius: ${CARD_BORDER_RADIUS_2024};
  background-color: var(--c-nd-50);
`;

const QUERY = gql`
  query EmptyCollectionSlotContentQuery($id: ID!) {
    cardCollectionSlot(id: $id) {
      id
      anyPlayer {
        slug
        displayName
      }
      serialNumber
      rarity
      customCardEditionName
      anyTeam {
        slug
        name
      }
      cardSet {
        slug
        ...CardSetChip_cardSet
      }
      cardPacks {
        slug
        ...FindItInCardPacks_cardPack
      }
    }
  }
  ${FindItInCardPacks.fragments.cardPack}
  ${CardSetChip.fragments.cardSet}
` as TypedDocumentNode<
  EmptyCollectionSlotContentQuery,
  EmptyCollectionSlotContentQueryVariables
>;
export const EmptyCollectionSlotContent = ({ id }: { id: string }) => {
  const { data } = useQuery(QUERY, {
    variables: { id },
  });
  const cardCollectionSlot = data?.cardCollectionSlot;

  const closeDialog = useCloseDialog();

  if (cardCollectionSlot === undefined) return null;

  const cardPacks = cardCollectionSlot.cardPacks ?? [];

  return (
    <CardPageLayout
      header={
        <Centered
          left={
            <IconButton
              icon={iconAngleLeft}
              color="tertiary"
              size="small"
              onClick={() => {
                closeDialog(DialogKey.slot);
              }}
            />
          }
        >
          <Vertical center>
            <HeadlineS bold>
              {cardCollectionSlot.anyPlayer.displayName}
            </HeadlineS>
            {!!cardCollectionSlot.cardSet && (
              <CardSetChip cardSet={cardCollectionSlot.cardSet} />
            )}
          </Vertical>
        </Centered>
      }
      preview={
        <EmptyCardPlaceholder center>
          <Chip style={{ marginTop: '140px' }}>
            <FormattedMessage defaultMessage="Not yet acquired" />
          </Chip>
        </EmptyCardPlaceholder>
      }
      setSlug={cardCollectionSlot.cardSet?.slug}
    >
      {!!cardPacks.length && <FindItInCardPacks cardPacks={cardPacks} />}
    </CardPageLayout>
  );
};

export const EmptyCollectionSlotDialog = () => {
  const id = useDialogParam(DialogKey.slot);
  return <EmptyCollectionSlotContent id={id || ''} />;
};
